<script setup lang="ts">
/**
 * Booking tab. Desktop renders the types pane as a `DynamicTable`; mobile
 * renders it as a tappable list (`BookingTypeMobileCell`) that opens
 * `BookingTypeMobileScreen` as a full-screen overlay, matching
 * `ServiceCategoriesTab`'s desktop/mobile split for the same kind of
 * entity. The schedule pane (`BookingScheduleView`) handles its own
 * mobile/desktop layout internally instead of branching here.
 */
import { computed, markRaw, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import AddButton from '@/components/table/AddButton.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import TextCell from '@/components/table/cell/TextCell.vue'
import NameCell from '@/components/table/cell/NameCell.vue'
import BookingTypeMobileCell from '@/components/table/cell/BookingTypeMobileCell.vue'
import { useIsVisible } from '@/composables/useIsVisible'
import {
  deleteBookingType,
  fetchBookingTypes,
  isBookingTypeHasBookingsError,
} from '@/api/bookingTypes'
import { bookingTypesPollingInterval } from '@/config/api'
import { useAddBookingTypeFormStore } from '@/stores/add_booking_type_form_store'
import { useBookingTypeCardStore } from '@/stores/booking_type_card_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import BookingScheduleView from '@/components/main/booking/BookingScheduleView.vue'
import BookingTypeMobileScreen from '@/components/main/bookingTypeCard/BookingTypeMobileScreen.vue'

const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()

type Pane = 'schedule' | 'types'
const activePane = ref<Pane>('schedule')

const headerRef = ref<HTMLElement | null>(null)
const isHeaderVisible = useIsVisible(headerRef)

// ── Types pane (table) ──────────────────────────────────────────────────────

interface BookingTypeRow {
  // Row for the booking types table, mapped from GET `/booking-types`.

  id: string
  rowNumber: number
  name: string
  // Display name — a null backend name is substituted with a placeholder
  // (see `data` below), so edit must use `rawName`, not this, to avoid
  // prefilling the form with that placeholder text as if it were real.
  rawName: string | null
  description?: string
  address?: string
}

const columns = computed<Column<BookingTypeRow>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: row.rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'name',
    label: t('common.name'),
    component: markRaw(NameCell),
    props: (row) => ({ name: row.name }),
    width: '14rem',
  },
  {
    key: 'description',
    label: t('common.description'),
    component: markRaw(TextCell),
    props: (row) => ({ value: row.description, emptyPlaceholder: '–' }),
    width: '20rem',
  },
  {
    key: 'address',
    label: t('tabs.booking.address'),
    component: markRaw(TextCell),
    props: (row) => ({ value: row.address, emptyPlaceholder: '–' }),
    width: '13rem',
  },
])

const mobileColumns: Column<BookingTypeRow>[] = [
  {
    key: 'name',
    label: '',
    component: markRaw(BookingTypeMobileCell),
    props: (row) => ({ name: row.name, description: row.description, address: row.address }),
  },
]

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['bookingTypes'],
  queryFn: ({ pageParam }) => fetchBookingTypes(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  // Matches useAllBookingTypes.ts's own staleTime for the identical
  // ['bookingTypes'] key: the two observers share cached data, so differing
  // staleTime would make this table's mount/remount consider it stale (and
  // refetch) sooner than the other observer's own polling interval already
  // guarantees.
  staleTime: bookingTypesPollingInterval,
  refetchInterval: bookingTypesPollingInterval,
  refetchIntervalInBackground: false,
})

const data = computed<BookingTypeRow[]>(() => {
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  let index = 0
  return pages.flatMap((page) =>
    (page.items ?? []).map((bt) => {
      index += 1
      const description =
        typeof bt.description === 'string' && bt.description.trim() !== ''
          ? bt.description
          : undefined
      const address =
        typeof bt.address === 'string' && bt.address.trim() !== '' ? bt.address : undefined
      return {
        id: bt.id,
        rowNumber: index,
        name: bt.name ?? t('cards.bookingType.defaultTypeName'),
        rawName: bt.name,
        description,
        address,
      }
    }),
  )
})

const addBookingTypeFormStore = useAddBookingTypeFormStore()
const bookingTypeCardStore = useBookingTypeCardStore()

function rowKeyFn(row: unknown): string {
  return (row as BookingTypeRow).id
}

const selectedRowKey = computed<string | null>(() => bookingTypeCardStore.selectedBookingTypeId)

function onAddType(): void {
  clearInfoViewSelections('addBookingTypeForm')
  addBookingTypeFormStore.open()
}

function onRowClick(row: BookingTypeRow): void {
  clearInfoViewSelections('bookingType')
  bookingTypeCardStore.selectBookingType(row.id)
}

const queryClient = useQueryClient()
const deleteMutation = useMutation({
  mutationFn: (id: string) => deleteBookingType(id),
})

function onRowDeleteHandler(row: unknown): void {
  const r = row as BookingTypeRow
  if (!confirm(t('tabs.booking.confirmDeleteType', { name: r.name }))) return
  deleteMutation.mutate(r.id, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bookingTypes'] })
      void queryClient.invalidateQueries({ queryKey: ['bookingSchedule'] })
      if (bookingTypeCardStore.selectedBookingTypeId === r.id) bookingTypeCardStore.clearSelection()
    },
    onError: (error) => {
      alert(
        isBookingTypeHasBookingsError(error)
          ? t('cards.bookingType.deleteHasBookings')
          : t('cards.bookingType.deleteFailed'),
      )
    },
  })
}

function onRowEditHandler(row: unknown): void {
  const r = row as BookingTypeRow
  clearInfoViewSelections('addBookingTypeForm')
  addBookingTypeFormStore.openForEdit(r.id, {
    name: r.rawName,
    description: r.description ?? null,
    address: r.address ?? null,
  })
}
</script>

<template>
  <article>
    <div ref="headerRef" class="tab-header" :class="{ 'tab-header--mobile': isMobile }">
      <div class="pane-tabs" role="group" :aria-label="t('tabs.booking.paneGroupLabel')">
        <button
          type="button"
          class="pane-tab"
          :class="{ 'pane-tab--active': activePane === 'schedule', 'pane-tab--mobile': isMobile }"
          @click="activePane = 'schedule'"
        >
          {{ t('tabs.booking.scheduleTab') }}
        </button>
        <button
          type="button"
          class="pane-tab"
          :class="{ 'pane-tab--active': activePane === 'types', 'pane-tab--mobile': isMobile }"
          @click="activePane = 'types'"
        >
          {{ t('tabs.booking.typesTab') }}
        </button>
      </div>
      <AddButton
        v-if="activePane === 'types' && hasRole(ResourceAccessRole.BookingTypesCreate)"
        :label="t('tabs.booking.addType')"
        @click="onAddType"
      />
    </div>

    <BookingScheduleView v-if="activePane === 'schedule'" />

    <template v-else>
      <p v-if="isPending" class="text-tufte-muted">{{ t('tabs.booking.loading') }}</p>
      <p v-else-if="isError" class="text-red-600">
        {{
          t('tabs.booking.loadError', {
            message: (error as Error)?.message ?? t('common.unknownError'),
          })
        }}
      </p>
      <DynamicTable
        v-else
        :class="isMobile ? 'booking-types-table--mobile' : 'booking-types-table'"
        :columns="(isMobile ? mobileColumns : columns) as Column<unknown>[]"
        :data="data"
        :has-next-page="hasNextPage"
        :is-fetching-next-page="isFetchingNextPage"
        :on-load-more="fetchNextPage"
        :on-row-click="(row) => onRowClick(row as BookingTypeRow)"
        :row-key="rowKeyFn"
        :selected-row-key="isMobile ? null : selectedRowKey"
        :on-row-edit="
          isMobile || !hasRole(ResourceAccessRole.BookingTypesEdit) ? undefined : onRowEditHandler
        "
        :on-row-delete="
          isMobile || !hasRole(ResourceAccessRole.BookingTypesDelete)
            ? undefined
            : onRowDeleteHandler
        "
        :on-add="
          isMobile || !hasRole(ResourceAccessRole.BookingTypesCreate) ? undefined : onAddType
        "
        :pinnable-header="!isMobile"
        :compact-add-visible="!isMobile && !isHeaderVisible"
        storage-key="booking-types"
      />
    </template>

    <BookingTypeMobileScreen
      v-if="isMobile && bookingTypeCardStore.hasSelection"
      :booking-type-id="bookingTypeCardStore.selectedBookingTypeId!"
      @close="bookingTypeCardStore.clearSelection()"
    />
  </article>
</template>

<style scoped>
.tab-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.tab-header--mobile {
  padding: 0.75rem 1.25rem;
}

/*
 * The pane switch reads as a page heading, not a filter chip: same
 * font/size/weight as every other tab's `<h1>` title (Клиенты, Платежи,
 * Категории…), just two of them side by side. Only color tells the two
 * apart — the active pane matches the normal heading color, the inactive
 * one is muted, exactly like the design sketch's own active/inactive tabs
 * (just without its green-pill background, to stay visually consistent
 * with the rest of the app's plain-text headings).
 */
.pane-tabs {
  display: flex;
  align-items: baseline;
  gap: 1.6rem;
  flex-wrap: wrap;
}
.pane-tab {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-4xl);
  font-weight: 400;
  line-height: var(--line-height-tufte-tight);
  color: var(--color-tufte-muted);
}
.pane-tab:hover {
  color: var(--color-tufte-text);
}
.pane-tab--active {
  color: var(--color-tufte-text);
}
/*
 * Heading-scale text doesn't fit two-up on a phone width — shrink to the
 * design sketch's own mobile tab size instead (`.ttab` in
 * docs/sketches/mobile/booking-schedule.html).
 */
.pane-tab--mobile {
  font-size: 1.5rem;
}

/* Mobile types list: full-width, no thead, tighter horizontal padding —
 * same convention as ServiceCategoriesTab's mobile table. */
.booking-types-table--mobile :deep(.dynamic-table) {
  width: 100%;
}
.booking-types-table--mobile :deep(thead) {
  display: none;
}
.booking-types-table--mobile :deep(td) {
  padding: 0.85rem 1.25rem;
}
</style>
